import React, { useState } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { VolumeX, Volume2, Shield, Trophy, Swords, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const SettingsSilenceRadio = () => {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();
  const [saving, setSaving] = useState(false);

  const isActive = !!user?.silence_radio;

  const handleToggle = async (checked) => {
    setSaving(true);
    try {
      const res = await api.put('/users/profile', { silence_radio: checked });
      updateUser(res.data);
      toast.success(
        checked
          ? (language === 'fr' ? 'Silence Radio actif. Discipline silencieuse.' : 'Radio Silence active. Silent discipline.')
          : (language === 'fr' ? 'Silence Radio desactive. La guerre reprend.' : 'Radio Silence off. War resumes.')
      );
    } catch {
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const mutedItems = language === 'fr'
    ? ['Alertes de Shift Tactique', 'MVP de la Semaine', 'Resultats Clash / Defis collectifs', 'Celebrations collectives']
    : ['Tactical Shift Alerts', 'Weekly MVP', 'Clash / Collective challenge results', 'Collective celebrations'];

  const activeItems = language === 'fr'
    ? ['Rappels de repas et hydratation', 'Debrief vendredi', 'Rapport mensuel personnel', 'Messages du Coach']
    : ['Meal & hydration reminders', 'Friday debrief', 'Personal monthly report', 'Coach messages'];

  return (
    <Card
      className={`bg-card border-border transition-all duration-300 ${isActive ? 'border-amber-500/50 shadow-lg shadow-amber-500/10' : ''}`}
      data-testid="settings-silence-radio"
    >
      <CardHeader>
        <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
          {isActive
            ? <VolumeX className="w-5 h-5 text-amber-400" />
            : <Volume2 className="w-5 h-5 text-muted-foreground" />
          }
          {language === 'fr' ? 'Mode Silence Radio' : 'Radio Silence Mode'}
        </CardTitle>
        <CardDescription>
          {language === 'fr'
            ? 'Desactive toutes les notifications de gamification. Reste concentre sur ton plan personnel.'
            : 'Disable all gamification notifications. Stay focused on your personal plan.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Toggle */}
        <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl">
          <div className="flex items-center gap-3">
            <Shield className={`w-5 h-5 ${isActive ? 'text-amber-400' : 'text-muted-foreground'}`} />
            <Label htmlFor="silence-radio-toggle" className="text-sm font-medium cursor-pointer">
              {language === 'fr' ? 'Activer le Silence Radio' : 'Enable Radio Silence'}
            </Label>
          </div>
          <Switch
            id="silence-radio-toggle"
            data-testid="silence-radio-toggle"
            checked={isActive}
            onCheckedChange={handleToggle}
            disabled={saving}
          />
        </div>

        {/* Breakdown: Muted vs Active */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Muted */}
          <div className={`p-3 rounded-xl border ${isActive ? 'border-red-500/30 bg-red-500/5' : 'border-border bg-secondary/30'}`}>
            <p className={`text-[10px] uppercase tracking-[0.2em] mb-2 font-bold ${isActive ? 'text-red-400' : 'text-muted-foreground'}`}>
              {language === 'fr' ? 'Mute' : 'Muted'}
            </p>
            <ul className="space-y-1.5">
              {mutedItems.map((item, i) => {
                const icons = [AlertTriangle, Trophy, Swords, Swords];
                const Icon = icons[i] || Swords;
                return (
                  <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon className={`w-3 h-3 ${isActive ? 'text-red-400/60' : 'text-muted-foreground/40'}`} />
                    <span className={isActive ? 'line-through opacity-50' : ''}>{item}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Still Active */}
          <div className={`p-3 rounded-xl border ${isActive ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border bg-secondary/30'}`}>
            <p className={`text-[10px] uppercase tracking-[0.2em] mb-2 font-bold ${isActive ? 'text-emerald-400' : 'text-muted-foreground'}`}>
              {language === 'fr' ? 'Toujours actif' : 'Still active'}
            </p>
            <ul className="space-y-1.5">
              {activeItems.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <div className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-400' : 'bg-muted-foreground/30'}`} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SettingsSilenceRadio;
